import React from 'react';
import { X, ChevronLeft, ChevronRight, LocateFixed, SplinePointerIcon } from 'lucide-react';

const formatStation = (station) => {
  if (station === null || station === undefined) return 'N/A';
  const hundreds = Math.floor(station / 100);
  const remainder = station % 100;
  return `${hundreds}+${remainder.toFixed(1).padStart(4, '0')}`;
};

export default function SegmentInfoPanel({
  segment,
  onClose,
  allSegments,
  onSelectSegment,
  iriColorScale,
  alrCount = 0,
  onViewALRs,
  onShowChart,
  isReversed = false
}) {
  if (!segment) return null;

  const ranges = iriColorScale?.ranges || [];

  const getIRIRating = (iri) => {
    for (const range of ranges) {
      if (iri <= range.max) return range.label;
    }
    return 'Unknown';
  };

  const getIRIColor = (iri) => {
    for (const range of ranges) {
      if (iri <= range.max) return range.color;
    }
    return iriColorScale?.defaultColor || '#A0A0A0';
  };

  const getIRIColorRGB = (iri) => {
    const hexColor = getIRIColor(iri);
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    return `${r}, ${g}, ${b}`;
  };

  // Calculate Indices
  const currentIndex = allSegments?.findIndex(s => s.segment_id === segment.segment_id) ?? -1;
  const totalLen = allSegments?.length ?? 0;

  // --- DIRECTION LOGIC ---
  const hasPrevious = isReversed
    ? currentIndex < totalLen - 1
    : currentIndex > 0;

  const hasNext = isReversed
    ? currentIndex > 0
    : currentIndex >= 0 && currentIndex < totalLen - 1;

  const handlePrevious = () => {
    if (!allSegments) return;
    const targetIndex = isReversed ? currentIndex + 1 : currentIndex - 1;
    if (targetIndex >= 0 && targetIndex < totalLen) {
      onSelectSegment(allSegments[targetIndex]);
    }
  };

  const handleNext = () => {
    if (!allSegments) return;
    const targetIndex = isReversed ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex >= 0 && targetIndex < totalLen) {
      onSelectSegment(allSegments[targetIndex]);
    }
  };

  const iri = segment.iri_avg_in_per_mi || segment.iri_in_per_mi || 0;
  const rating = getIRIRating(iri);
  const colorRgb = getIRIColorRGB(iri);
  const lengthFt = segment.distance_ft || 0;

  // --- DISPLAY STATION LOGIC ---
  const displayStartStation = isReversed ? segment.to_station_ft : segment.from_station_ft;
  const displayEndStation = isReversed ? segment.from_station_ft : segment.to_station_ft;

  return (
    <div className="absolute top-3 right-3 bg-white/90 text-gray-800 rounded-lg shadow-lg border border-gray-200 z-[1002] pointer-events-auto backdrop-blur-sm">
      <div className="flex items-center gap-2 px-2 py-1.5">

        {/* Prev/Next */}
        <div className="flex rounded-md overflow-hidden border border-gray-300 shrink-0">
          <button
            onClick={handlePrevious}
            disabled={!hasPrevious}
            className="px-2 py-1.5 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 disabled:opacity-30 transition-colors"
          >
            <ChevronLeft size={16} />
          </button>
          <div className="w-px bg-gray-300" />
          <button
            onClick={handleNext}
            disabled={!hasNext}
            className="px-2 py-1.5 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 disabled:opacity-30 transition-colors"
          >
            <ChevronRight size={16} />
          </button>
        </div>

        {/* Tier */}
        <div className="w-24 flex items-center justify-center gap-1.5 shrink-0">
          <span
            className="w-2.5 h-2.5 rounded-full shrink-0"
            style={{ backgroundColor: `rgb(${colorRgb})`, boxShadow: `0 0 6px rgb(${colorRgb}, 0.6)` }}
          />
          <span className="text-xs font-semibold text-gray-700">{rating}</span>
        </div>

        <div className="w-px h-5 bg-gray-300 shrink-0" />

        {/* MRI Value */}
        <div className="w-20 flex items-baseline justify-center gap-1 shrink-0">
          <span className="text-lg font-bold leading-none text-gray-800 font-mono tabular-nums">
            {iri.toFixed(1)}
          </span>
          <span className="text-[10px] text-gray-400 font-medium">in/mi</span>
        </div>

        <div className="w-px h-5 bg-gray-300 shrink-0" />

        {/* Station Range */}
        <div className="w-52 flex items-center justify-center gap-1 text-xs shrink-0">
          <span className="text-gray-500">Sta</span>
          <span className="font-mono tabular-nums text-gray-700">{formatStation(displayStartStation)}</span>
          <span className="text-gray-400">—</span>
          <span className="font-mono tabular-nums text-gray-700">{formatStation(displayEndStation)}</span>
        </div>

        <div className="w-px h-5 bg-gray-300 shrink-0" />

        {/* Length & Speed */}
        <div className="w-24 flex items-center justify-center gap-2 text-xs shrink-0">
          <span className="font-mono tabular-nums text-gray-500">{lengthFt.toFixed(0)} ft</span>
          {segment.avg_speed_mph && (
            <span className="font-mono tabular-nums text-gray-500">{segment.avg_speed_mph.toFixed(0)} mph</span>
          )}
        </div>

        <div className="w-px h-5 bg-gray-300 shrink-0" />

        {/* Action Buttons */}
        <div className="w-16 flex items-center justify-center gap-1 shrink-0">
          <button
            onClick={onShowChart}
            className="p-1.5 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
            title="Show on Chart"
          >
            <SplinePointerIcon size={14} />
          </button>
          <button
            onClick={onViewALRs}
            disabled={alrCount === 0}
            className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
              alrCount > 0
                ? 'text-blue-600 hover:bg-blue-50'
                : 'text-gray-400 cursor-not-allowed'
            }`}
          >
            <LocateFixed size={12} />
            <span>{alrCount}</span>
          </button>
        </div>

        {/* Close */}
        <button
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-700 transition-colors shrink-0"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
